/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useRef } from 'react'
import { ArrowCaret } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { useQueryParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import {
  getPersonName,
  getPrimaryRelationshipText,
  getRelationshipToPrimaryPersonDisplayLabel,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useR2StateContext, useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import useHoverHighlights from './useHoverHighlights'
import { getIconByAttachmentStatus, getCheckboxElement, getIconElement } from './PersonDisplay.utils'
import { usePersonAttachmentData } from '../../hooks/usePersonAttachmentData'

// Note: We get React warnings because is the parent of this component and we have above and has wrapping the child components.
// For now we are making startElement and endElement lower case to fix some warnings. We need to refactor this file to follow React best practices

// With the DragNames Panel refactor, this component is no longer responsible for handling multiselect and checkboxes.
// When removing the rip_sharedImageViewer_dragNamesPanelRefactor flag, these props should also be removed:
// isMultiSelect and onCheckboxChange.
export default function PersonDisplay({
  person,
  record,
  openNewPage,
  isSelected,
  onPersonSelect = () => {},
  endelement = <ListItem.IconButton Icon={ArrowCaret} />,
  startelement,
  disableDefaultClickHandler = false,
  isMultiSelect = false,
  onCheckboxChange = () => {},
}) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { openOverlay } = useOverlayContext()
  const { setQuery } = useQueryParams()
  const { highlightPersonNameHighlight, highlightRecordHighlight, resetPersonListHighlight, resetRecordListHighlight } =
    useHoverHighlights()
  const listItemRef = useRef()
  const r2SelectedPersonIdRef = useRef()
  if (isSelected && r2SelectedPersonIdRef.current !== r2State.selectedPersonId && listItemRef.current) {
    // Auto-scroll behavior disabled due to interference with user interaction
    // listItemRef.current.scrollIntoView({
    //   behavior: 'smooth',
    //   block: 'center',
    // })
    r2SelectedPersonIdRef.current = r2State.selectedPersonId
  }
  const { attachment, attachmentOrMatchId } = usePersonAttachmentData(person?.id)

  const dragNamesPanelRefactorFlag = useFeatureFlag('rip_sharedImageViewer_dragNamesPanelRefactor')
  const personName = getPersonName({ person })
  const primaryRelationshipLabel = `${getRelationshipToPrimaryPersonDisplayLabel(person, zionSlsState.types, record)}`
  const relationshipText = `${getRelationshipToPrimaryPersonText(person, zionSlsState.types, record, t)}`
  const primaryRelationshipText = getPrimaryRelationshipText(primaryRelationshipLabel, relationshipText)

  const hintStartingIcon = getIconByAttachmentStatus(attachment)

  let defaultStartElement = <ListItem.Filler width={40} />

  if (dragNamesPanelRefactorFlag.isOn) {
    defaultStartElement = attachmentOrMatchId ? (
      getIconElement(hintStartingIcon, attachmentOrMatchId, attachment)
    ) : (
      <ListItem.Filler width={40} />
    )
  } else if (isMultiSelect) {
    // Remove this when removing the flag
    defaultStartElement = getCheckboxElement(person, isSelected, onCheckboxChange, t, personName)
  } else if (attachmentOrMatchId) {
    // Remove this when removing the flag
    defaultStartElement = getIconElement(hintStartingIcon, attachmentOrMatchId, attachment)
  }

  const handleClick = useCallback(() => {
    onPersonSelect(person?.id)
    if (openNewPage) {
      openNewPage()
    } else {
      openOverlay('viewIndex')
      r2Dispatch({
        type: 'NAV_SELECT_TO_VIEW',
        value: { personId: person?.id, recordId: record?.id },
      })
      trackEvent({
        event_name: 'click_action',
        link_name: `Edit: Select Person`,
      })
      setQuery({ personArk: `/ark:/61903/${person?.id}`, action: 'view' })
    }
  }, [onPersonSelect, openNewPage, openOverlay, person?.id, r2Dispatch, record?.id, setQuery])

  const handleMouseEnter = useCallback(() => {
    highlightPersonNameHighlight({ person })
    highlightRecordHighlight({ record })
  }, [highlightPersonNameHighlight, highlightRecordHighlight, person, record])
  const handleMouseLeave = useCallback(() => {
    resetPersonListHighlight()
    resetRecordListHighlight()
  }, [resetPersonListHighlight, resetRecordListHighlight])
  const endelementCaretButtonTitle = `${t('person-display.button.person-select', 'Click to view')} ${personName}`
  const enhancedEndElement = React.cloneElement(endelement, {
    title: endelementCaretButtonTitle,
    onClick: disableDefaultClickHandler ? () => {} : handleClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  })
  return (
    <>
      {person && (
        <ListItem
          ref={listItemRef}
          data-testid={personName}
          key={person?.id}
          overline={primaryRelationshipText}
          primaryText={personName}
          emphasized={!!person?.primary}
          startElement={startelement || defaultStartElement}
          endElement={enhancedEndElement}
          onClick={disableDefaultClickHandler ? () => {} : handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          selected={isSelected}
        />
      )}
      {!person && <></>}
    </>
  )
}
